import { auth } from '../config/firebase';
import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

async function authHeaders(): Promise<Record<string, string>> {
  const user = auth.currentUser;
  if (!user) return {};
  const token = await user.getIdToken();
  return { Authorization: `Bearer ${token}` };
}

export interface MarketDataSale {
  _id: string;
  eventId: string;
  eventName?: string | null;   // denormalised at gather time
  showDate?: string | null;
  saleKey: string;
  category: string;
  section?: string;          // specific block within the category — only present on some sales
  allocation: string;        // "Home" | "Away"
  splitAs: string;           // e.g. "Singles"
  seatingArrangement: string; // e.g. "Single Seat(s)", "Up To 4 Together"
  shippingType: string;
  currency: string;
  currentBroker: boolean;    // true = my own sale
  quantity: number;          // tickets in this sale
  sellAmount: number;        // PER-TICKET price
  restrictions: string[];
  createdOn: string;         // ISO date
  gatheredAt: string;        // ISO date
  // 'stubhub' on StubHub sales; absent on every Live row gathered before the field existed,
  // so missing means Live.
  source?: string | null;
}

// The server sends EVERY sale as a compact payload, {v:2, cols:[...], rows:[[...], ...]}, gzipped
// on the wire (the browser decompresses it). Rebuild the MarketDataSale objects the screen uses.
// _id is synthesised so list keys are stable; the server does not send it. The old plain-array
// shape is still accepted, so a client deployed ahead of the server keeps working.
type CompactSales = { v: 2; cols: string[]; rows: unknown[][]; counts?: { live: number; stubhub: number } };

export async function getMarketDataSales(): Promise<MarketDataSale[]> {
  const headers = await authHeaders();
  const { data } = await axios.get<MarketDataSale[] | CompactSales>(`${API_URL}/market-data/sales`, { headers });
  if (Array.isArray(data)) return data;
  const { cols, rows } = data;
  const out: MarketDataSale[] = new Array(rows.length);
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i];
    const o: Record<string, unknown> = { _id: `s${i}` };
    for (let c = 0; c < cols.length; c++) if (row[c] !== null) o[cols[c]] = row[c];
    out[i] = o as unknown as MarketDataSale;
  }
  // Newest first, as before: the server streams rows in storage order.
  out.sort((a, b) => (a.createdOn < b.createdOn ? 1 : a.createdOn > b.createdOn ? -1 : 0));
  return out;
}

export interface MarketDataEvent {
  eventId: string;
  eventName: string;
  showDate: string | null;
  alternativeIds?: string[];
}

export async function getMarketDataEvents(): Promise<MarketDataEvent[]> {
  const headers = await authHeaders();
  const { data } = await axios.get<MarketDataEvent[]>(`${API_URL}/market-data/events`, { headers });
  return data;
}

// ── On-demand loading ────────────────────────────────────────────────────────────────────────────
// The screen no longer downloads every sale on open. It loads the game list (fixtures) and the
// latest sales, and fetches one game's sales only when that game is picked.

export interface MarketDataFixture {
  eventName: string;
  day: string;          // YYYY-MM-DD (UTC) of the show date — the same identity as the client's fixtureKey
  showDate: string;
  live: number;         // sales stored for this fixture, per marketplace
  stubhub: number;
  last: string | null;  // newest sale
}

let seq = 0;
const withIds = (rows: MarketDataSale[]): MarketDataSale[] =>
  rows
    .map((r) => ({ ...r, _id: r._id || `q${seq++}` }))
    .sort((a, b) => (a.createdOn < b.createdOn ? 1 : a.createdOn > b.createdOn ? -1 : 0));

export async function getMarketDataFixtures(): Promise<MarketDataFixture[]> {
  const headers = await authHeaders();
  const { data } = await axios.get<MarketDataFixture[]>(`${API_URL}/market-data/fixtures`, { headers });
  return data;
}

/** Every sale of one fixture (both marketplaces). */
export async function getFixtureSales(eventName: string, day: string): Promise<MarketDataSale[]> {
  const headers = await authHeaders();
  const { data } = await axios.get<MarketDataSale[]>(`${API_URL}/market-data/fixture`, { headers, params: { name: eventName, day } });
  return withIds(data);
}

/** Every sale of one home team's games. */
export async function getTeamSales(home: string): Promise<MarketDataSale[]> {
  const headers = await authHeaders();
  const { data } = await axios.get<MarketDataSale[]>(`${API_URL}/market-data/team`, { headers, params: { home } });
  return withIds(data);
}

/** Newest sales per marketplace, for the "Latest sales" feed. */
export async function getLatestSales(n = 60): Promise<MarketDataSale[]> {
  const headers = await authHeaders();
  const { data } = await axios.get<MarketDataSale[]>(`${API_URL}/market-data/latest`, { headers, params: { n } });
  return withIds(data);
}
